/* ═══════════════════════════════════════════════════════════════
   SpacingBand — the Studio's ONE spacing affordance.

   A band that literally FILLS the gap it edits: its top edge is the
   bottom of the paragraph above, its bottom edge is the top of the
   paragraph below, both read off the live layout with
   `getBoundingClientRect()` rather than predicted from the CSS the
   config produced (see spacing-geometry.ts for why prediction was
   wrong). Josh's test: "the blue band IS the gap."

   One component serves both users of the language:
   - a level's Space Before / Between / After (StudioBody)
   - END OF SECTION's space before (StudioCanvas)

   It renders absolutely INSIDE a positioned host, so the host must
   carry `position: relative`.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { bandHitPadding, gapGeometry, measuredScale, pxToPt } from './spacing-geometry';

export interface SpacingBandProps {
  /** The positioned ancestor the band is drawn inside. */
  hostRef: React.RefObject<HTMLElement | null>;
  /** The element whose BOTTOM is the top of the gap (null → host top). */
  getAbove: () => Element | null;
  /** The element whose TOP is the bottom of the gap (null → host bottom). */
  getBelow: () => Element | null;
  /** The pt value this band reads and writes. */
  valuePt: number;
  /** "Space before", "Space between", … — used in the readout and label. */
  label: string;
  /** Screen-reader label; falls back to `label`. */
  ariaLabel?: string;
  min?: number;
  max?: number;
  /** Commit a new pt value (already unclamped — the band clamps first). */
  onCommit: (v: number) => void;
  /** Left inset in host layout px, so a band lines up with its own level. */
  leftPx?: number;
  /** Rendered instead of the drag band — e.g. "No space same" is on. */
  frozenNote?: string;
}

export function SpacingBand({
  hostRef,
  getAbove,
  getBelow,
  valuePt,
  label,
  ariaLabel,
  min = 0,
  max = 120,
  onCommit,
  leftPx = 0,
  frozenNote,
}: SpacingBandProps) {
  const [geo, setGeo] = React.useState<{ top: number; height: number } | null>(null);
  const [hover, setHover] = React.useState(false);
  const [drag, setDrag] = React.useState(false);
  const scaleRef = React.useRef(1);
  const lit = hover || drag;

  // Re-measured after EVERY render (a forma edit re-renders the whole
  // canvas, and margin collapsing means a neighbouring level's change can
  // move THIS gap). The equality guard stops the state write — and so the
  // render loop — as soon as the geometry settles. A dep list is deliberately
  // absent: it would pin the measurement to props, and the gap moves for
  // reasons no prop describes (a sibling level's margin, a font swap).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const hostRect = host.getBoundingClientRect();
    const scale = measuredScale(hostRect.width, host.offsetWidth);
    scaleRef.current = scale;
    const a = getAbove()?.getBoundingClientRect() ?? null;
    const b = getBelow()?.getBoundingClientRect() ?? null;
    const next = gapGeometry(hostRect, a, b, scale);
    setGeo((prev) =>
      prev && Math.abs(prev.top - next.top) < 0.02 && Math.abs(prev.height - next.height) < 0.02
        ? prev
        : next
    );
  });

  // Window resize / dock resize / font load — anything that reflows the
  // paper without a React render still has to move the band.
  React.useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const bump = () => setGeo(null);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(bump) : null;
    ro?.observe(host);
    window.addEventListener('resize', bump);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', bump);
    };
  }, [hostRef]);

  const commit = React.useCallback(
    (v: number) => onCommit(Math.max(min, Math.min(max, Math.round(v)))),
    [onCommit, min, max]
  );

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startVal = valuePt;
    setDrag(true);
    // 1:1 with the pointer, in LAYOUT space — the band's own bottom edge
    // tracks the cursor, which is the whole promise of "the band is the
    // gap". Zoom is divided out so a drag feels identical at any zoom.
    const onMove = (ev: MouseEvent) =>
      commit(startVal + pxToPt((ev.clientY - startY) / (scaleRef.current || 1)));
    const onUp = () => {
      setDrag(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!geo) return null;

  if (frozenNote) {
    return (
      <div
        className="sfs-gapband is-frozen"
        style={{ top: geo.top, height: geo.height, left: leftPx }}
        aria-hidden="true"
      >
        <span className="sfs-gapband-note">{frozenNote}</span>
      </div>
    );
  }

  const measuredPt = Math.round(pxToPt(geo.height));
  const pad = bandHitPadding(geo.height);

  return (
    <div
      className={`sfs-gapband${lit ? ' is-lit' : ''}${geo.height > 0.5 ? ' has-space' : ''}`}
      style={{ top: geo.top, height: geo.height, left: leftPx }}
    >
      <div
        role="slider"
        tabIndex={0}
        aria-label={ariaLabel || label}
        aria-valuenow={valuePt}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuetext={`${valuePt} points`}
        title={`${label} — drag to resize (${valuePt}pt)`}
        className="sfs-gapband-grip"
        style={{ top: -pad, height: geo.height + pad * 2 }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseDown={startDrag}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            commit(valuePt + (e.key === 'ArrowUp' ? 1 : -1));
          }
        }}
      />
      {lit && (
        <span className="sfs-gapband-readout">
          {label} · {valuePt}pt
          {measuredPt > valuePt + 1 && <em className="sfs-gapband-hint"> · gap {measuredPt}pt</em>}
        </span>
      )}
    </div>
  );
}
